<script setup>
import { computed, ref } from 'vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

// Aceleres 2026-09-15 (Facundo): folha grande de emojis do telemovel, unica
// para todos os "+" das reacoes rapidas (conversa, galeria, PDF, figurinhas) e
// para a legenda dos anexos. Vai para o `body` e cobre tudo, abre suavemente
// e fica sempre no tema claro (`acl-light-scope`). O puxador estica para cima,
// encolhe e fecha ao chegar ao rodape. So e usada em viewport mobile.
defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  onSelect: {
    type: Function,
    required: true,
  },
  onBackspace: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['close']);

const MIN_HEIGHT = 200;
const DISMISS_HEIGHT = 56;

const sheetRef = ref(null);
const height = ref(0);
const dragging = ref(false);
let dragMoved = false;
let dragStartY = 0;
let dragStartHeight = 0;

const sheetStyle = computed(() =>
  height.value ? { height: `${height.value}px` } : null
);

const close = () => emit('close');

// Cada abertura volta a altura padrao.
const resetHeight = () => {
  height.value = 0;
  dragging.value = false;
};

const onDragStart = event => {
  dragStartY = event.clientY;
  dragStartHeight = height.value || sheetRef.value?.offsetHeight || 0;
  if (!dragStartHeight) return;

  dragging.value = true;
  dragMoved = false;
  event.currentTarget.setPointerCapture?.(event.pointerId);
};

const onDragMove = event => {
  if (!dragging.value) return;

  dragMoved = true;
  const maxHeight = Math.round(window.innerHeight * 0.9);
  const next = dragStartHeight + dragStartY - event.clientY;
  height.value = Math.min(Math.max(next, 0), maxHeight);
};

const onDragEnd = () => {
  if (!dragging.value) return;
  dragging.value = false;
  if (!dragMoved) return;

  if (height.value <= DISMISS_HEIGHT) {
    close();
    return;
  }
  if (height.value < MIN_HEIGHT) height.value = MIN_HEIGHT;
};
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[2147483646] bg-black/30"
        aria-hidden="true"
        @click.stop="close"
      />
    </Transition>
    <Transition
      enter-active-class="animate-[fade-in-up_0.16s_ease-out_both]"
      leave-active-class="transition-transform duration-150 ease-in"
      leave-to-class="translate-y-full"
      @after-leave="resetHeight"
    >
      <div
        v-if="open"
        ref="sheetRef"
        class="acl-light-scope fixed inset-x-0 bottom-0 z-[2147483647] flex h-[clamp(15rem,45dvh,22.5rem)] flex-col overflow-hidden rounded-t-xl bg-[#f7f8fa] pb-[env(safe-area-inset-bottom)] text-n-slate-12 shadow-[0_-6px_18px_rgba(0,0,0,0.12)]"
        :class="{ 'transition-[height] duration-150': !dragging }"
        :style="sheetStyle"
        role="dialog"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        @click.stop
      >
        <div
          class="flex min-h-9 flex-none cursor-grab touch-none items-center justify-center pb-1 pt-2"
          aria-hidden="true"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragEnd"
        >
          <span class="h-1.5 w-10 rounded-full bg-n-slate-8" />
        </div>
        <!-- O CSS global fixa o card em 280px e a grade em 380px com
             !important; estes seletores mais especificos devolvem as duas
             alturas ao flex da folha, entao ao esticar so a grade cresce e os
             botoes ficam no lugar. -->
        <div
          class="aceleres-emoji-picker-card flex min-h-0 flex-1 flex-col [&.aceleres-emoji-picker-card]:!h-auto [&.aceleres-emoji-picker-card]:!max-h-none [&.aceleres-emoji-picker-card_.aceleres-emoji-picker-body_.emoji-item]:!h-auto [&.aceleres-emoji-picker-card_.aceleres-emoji-picker-body_.emoji-item]:!max-h-none"
        >
          <div class="aceleres-emoji-picker-body min-h-0 flex-1">
            <EmojiInput
              embedded
              show-recent-reactions
              mobile-sheet
              scroll-sync-tabs
              :show-search-icon="false"
              :focus-search-on-mount="false"
              :on-click="onSelect"
              :on-backspace="onBackspace"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
